import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Copy, History, KeyRound, Loader2, Plus, ShieldAlert, Trash2 } from "lucide-react";
import { api, toastError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export default function ApiKeys() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [revealed, setRevealed] = useState(null); // { key, name, prefix }
  const [revoking, setRevoking] = useState(null);
  const [auditOf, setAuditOf] = useState(null); // {id, name}

  const fetchKeys = async () => {
    setLoading(true);
    try {
      const res = await api.get("/worker-keys");
      setKeys(res.data);
    } catch (err) {
      toastError(err, "Failed to load worker keys");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKeys();
  }, []);

  return (
    <div className="space-y-8" data-testid="api-keys-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Worker API keys</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Issue keys for VoxIVR voice workers to authenticate to this admin
            API. The plaintext is shown only once at generation.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="api-keys-add-btn">
          <Plus className="h-4 w-4" /> Generate key
        </Button>
      </div>

      <div className="rounded-lg border border-border bg-card">
        {loading ? (
          <div className="flex h-32 items-center justify-center text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : keys.length === 0 ? (
          <EmptyState onAdd={() => setCreateOpen(true)} />
        ) : (
          <Table data-testid="api-keys-table">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Prefix</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Last used</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {keys.map((k) => (
                <TableRow key={k.id} data-testid={`api-keys-row-${k.id}`}>
                  <TableCell className="font-medium">{k.name}</TableCell>
                  <TableCell className="font-mono text-xs">{k.prefix}…</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {new Date(k.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {k.last_used_at ? new Date(k.last_used_at).toLocaleString() : "—"}
                  </TableCell>
                  <TableCell>
                    {k.revoked ? (
                      <Badge variant="destructive">Revoked</Badge>
                    ) : (
                      <Badge>Active</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setAuditOf(k)}
                      data-testid={`api-keys-audit-${k.id}`}
                    >
                      <History className="h-4 w-4" /> Audit
                    </Button>
                    {!k.revoked && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setRevoking(k)}
                        data-testid={`api-keys-revoke-${k.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" /> Revoke
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <CreateKeyDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(payload) => {
          setRevealed(payload);
          fetchKeys();
        }}
      />

      <RevealedKeyDialog
        revealed={revealed}
        onClose={() => setRevealed(null)}
      />

      <AuditDialog auditOf={auditOf} onClose={() => setAuditOf(null)} />

      <AlertDialog open={!!revoking} onOpenChange={(o) => !o && setRevoking(null)}>
        <AlertDialogContent data-testid="api-keys-revoke-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke key?</AlertDialogTitle>
            <AlertDialogDescription>
              The worker using <b>{revoking?.name}</b> will lose access on its
              next call. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="api-keys-revoke-confirm"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                try {
                  await api.delete(`/worker-keys/${revoking.id}`);
                  toast.success("Key revoked");
                  setRevoking(null);
                  fetchKeys();
                } catch (err) {
                  toastError(err, "Failed to revoke");
                }
              }}
            >
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div
      className="flex flex-col items-center gap-3 py-16 text-center"
      data-testid="api-keys-empty"
    >
      <div className="rounded-full bg-secondary p-3">
        <KeyRound className="h-6 w-6 text-muted-foreground" />
      </div>
      <div className="text-base font-medium">No worker keys yet</div>
      <p className="max-w-sm text-sm text-muted-foreground">
        Generate one to authenticate your VoxIVR voice workers when they call
        back into this admin API. Each plaintext key is revealed exactly once.
      </p>
      <Button onClick={onAdd} className="mt-2" data-testid="api-keys-empty-add">
        <Plus className="h-4 w-4" /> Generate key
      </Button>
    </div>
  );
}

function CreateKeyDialog({ open, onOpenChange, onCreated }) {
  const {
    register: rhf,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({ defaultValues: { name: "" } });
  const [submitting, setSubmitting] = useState(false);

  const submit = async ({ name }) => {
    setSubmitting(true);
    try {
      const res = await api.post("/worker-keys", { name });
      onOpenChange(false);
      reset();
      onCreated(res.data);
    } catch (err) {
      toastError(err, "Failed to generate key");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="api-keys-create-dialog">
        <DialogHeader>
          <DialogTitle>Generate worker key</DialogTitle>
          <DialogDescription>
            Give this key a name describing the worker / environment using it.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(submit)} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              placeholder="prod-eu-west-1 worker"
              data-testid="api-keys-name"
              {...rhf("name", { required: "Name is required" })}
            />
            {errors.name && (
              <p className="text-xs text-destructive">{errors.name.message}</p>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting} data-testid="api-keys-generate">
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Generate
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RevealedKeyDialog({ revealed, onClose }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(revealed.plaintext_key);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Could not copy — select & copy manually");
    }
  };

  return (
    <Dialog open={!!revealed} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="api-keys-revealed-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-destructive" />
            Copy this key now
          </DialogTitle>
          <DialogDescription>
            <b>{revealed?.name}</b> · This is the only time the plaintext key
            will be shown. Store it in your worker's secrets manager
            immediately.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-md border border-border bg-muted p-3">
          <code
            className="block break-all font-mono text-xs"
            data-testid="api-keys-plaintext"
          >
            {revealed?.plaintext_key}
          </code>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={copy} data-testid="api-keys-copy">
            <Copy className="h-4 w-4" /> Copy
          </Button>
          <Button onClick={onClose} data-testid="api-keys-revealed-close">
            I've copied it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function AuditDialog({ auditOf, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!auditOf) {
      setData(null);
      return;
    }
    let cancel = false;
    setLoading(true);
    api
      .get(`/worker-keys/${auditOf.id}/audit`)
      .then((r) => {
        if (!cancel) setData(r.data);
      })
      .catch((err) => {
        if (!cancel) toastError(err, "Failed to load audit");
      })
      .finally(() => {
        if (!cancel) setLoading(false);
      });
    return () => {
      cancel = true;
    };
  }, [auditOf]);

  return (
    <Dialog open={!!auditOf} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="api-keys-audit-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-4 w-4" />
            {auditOf?.name || "Worker key"} — last 10 uses
          </DialogTitle>
          <DialogDescription>
            Ring buffer of the most recent worker-authenticated requests
            using this key. Most recent first.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex h-24 items-center justify-center text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : data?.recent_uses?.length ? (
          <ul className="divide-y divide-border max-h-80 overflow-auto rounded-md border border-border">
            {data.recent_uses.map((u, i) => (
              <li
                key={i}
                className="px-3 py-2 text-xs grid grid-cols-[10rem_8rem_1fr] gap-2"
                data-testid={`api-keys-audit-row-${i}`}
              >
                <span className="text-muted-foreground tabular-nums whitespace-nowrap">
                  {new Date(u.at).toLocaleString()}
                </span>
                <span className="font-mono">{u.source_ip || "—"}</span>
                <span className="font-mono break-all">{u.endpoint}</span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            No recorded uses yet — the worker hasn't called the API with
            this key.
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
